import React from 'react';

import {
  ErrorBanner,
  JourneySelectInput,
  JourneyTextArea,
  JourneyTextInput,
} from '../../../shared/components';

interface IPropertyRecord {
  id?: number;
  nameOfDeed?: string | null;
  areaDistrict?: string | null;
  farmArrangement?: string | null;
}

interface IPropertyFormState {
  nameOfDeed: string;
  areaDistrict: string;
  farmArrangement: string;
  term: string;
  amount: string;
  dryland: string;
  irrigation: string;
  pastures: string;
  grazing: string;
  intensive: string;
  other: string;
  notes: string;
}

export interface IPropertySubStepProps {
  propertyError: string | null;
  isLoadingProperties: boolean;
  isSavingProperties: boolean;
  propertyForm: IPropertyFormState;
  isOwnedPropertyArrangement: boolean;
  propertyRecords: IPropertyRecord[];
  onPropertyFormChange: (
    field: keyof IPropertyFormState,
  ) => (
    event: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >,
  ) => void;
  onAddPropertyRecord: () => void;
  onRemovePropertyRecord: (record: IPropertyRecord) => void | Promise<void>;
  onNormalizeFarmArrangement: (value: string) => 'OWNED' | 'LEASED' | '';
}

export const PropertySubStep: React.FC<IPropertySubStepProps> = ({
  propertyError,
  isLoadingProperties,
  isSavingProperties,
  propertyForm,
  isOwnedPropertyArrangement,
  propertyRecords,
  onPropertyFormChange,
  onAddPropertyRecord,
  onRemovePropertyRecord,
  onNormalizeFarmArrangement,
}) => (
  <div className="application-card">
    <div className="subform-stack">
      <header className="subform-stack__header">
        <h3>Property records</h3>
        <p>
          Capture each deed and land-use breakdown (dryland, irrigation, grazing
          and related notes).
        </p>
      </header>

      <ErrorBanner message={propertyError} />

      {isLoadingProperties ? (
        <div className="application-empty-state">
          <h3>Loading property records</h3>
        </div>
      ) : null}

      <div className="form-grid two-columns">
        <div className="form-field">
          <label className="form-label" htmlFor="name-of-deed">
            Name of deed
          </label>
          <JourneyTextInput
            id="name-of-deed"
            value={propertyForm.nameOfDeed}
            onChange={onPropertyFormChange('nameOfDeed')}
          />
        </div>

        <div className="form-field">
          <label className="form-label" htmlFor="area-district">
            Area district
          </label>
          <JourneyTextInput
            id="area-district"
            value={propertyForm.areaDistrict}
            onChange={onPropertyFormChange('areaDistrict')}
          />
        </div>

        <div className="form-field">
          <label className="form-label" htmlFor="farm-arrangement">
            Farm arrangement
          </label>
          <JourneySelectInput
            id="farm-arrangement"
            value={propertyForm.farmArrangement}
            onChange={onPropertyFormChange('farmArrangement')}
          >
            <option value="">Select arrangement</option>
            <option value="OWNED">Owned</option>
            <option value="LEASED">Leased</option>
          </JourneySelectInput>
        </div>

        <div className="form-field">
          <label className="form-label" htmlFor="lease-term">
            Lease term
          </label>
          <JourneyTextInput
            id="lease-term"
            type="number"
            min="0"
            step="1"
            value={propertyForm.term}
            placeholder={
              isOwnedPropertyArrangement
                ? 'Not applicable for owned property'
                : ''
            }
            disabled={isOwnedPropertyArrangement}
            onChange={onPropertyFormChange('term')}
          />
        </div>

        <div className="form-field">
          <label className="form-label" htmlFor="lease-amount">
            Lease amount
          </label>
          <JourneyTextInput
            id="lease-amount"
            type="number"
            min="0"
            value={propertyForm.amount}
            placeholder={
              isOwnedPropertyArrangement
                ? 'Not applicable for owned property'
                : ''
            }
            disabled={isOwnedPropertyArrangement}
            onChange={onPropertyFormChange('amount')}
          />
        </div>

        <div className="form-field">
          <label className="form-label" htmlFor="dryland-area">
            Dryland (ha)
          </label>
          <JourneyTextInput
            id="dryland-area"
            type="number"
            min="0"
            value={propertyForm.dryland}
            onChange={onPropertyFormChange('dryland')}
          />
        </div>

        <div className="form-field">
          <label className="form-label" htmlFor="irrigation-area">
            Irrigation (ha)
          </label>
          <JourneyTextInput
            id="irrigation-area"
            type="number"
            min="0"
            value={propertyForm.irrigation}
            onChange={onPropertyFormChange('irrigation')}
          />
        </div>

        <div className="form-field">
          <label className="form-label" htmlFor="pasture-area">
            Pastures (ha)
          </label>
          <JourneyTextInput
            id="pasture-area"
            type="number"
            min="0"
            value={propertyForm.pastures}
            onChange={onPropertyFormChange('pastures')}
          />
        </div>

        <div className="form-field">
          <label className="form-label" htmlFor="grazing-area">
            Grazing (ha)
          </label>
          <JourneyTextInput
            id="grazing-area"
            type="number"
            min="0"
            value={propertyForm.grazing}
            onChange={onPropertyFormChange('grazing')}
          />
        </div>

        <div className="form-field">
          <label className="form-label" htmlFor="intensive-area">
            Intensive (ha)
          </label>
          <JourneyTextInput
            id="intensive-area"
            type="number"
            min="0"
            value={propertyForm.intensive}
            onChange={onPropertyFormChange('intensive')}
          />
        </div>

        <div className="form-field">
          <label className="form-label" htmlFor="other-area">
            Other (ha)
          </label>
          <JourneyTextInput
            id="other-area"
            type="number"
            min="0"
            value={propertyForm.other}
            onChange={onPropertyFormChange('other')}
          />
        </div>
      </div>

      <div className="form-field">
        <label className="form-label" htmlFor="property-notes">
          Notes
        </label>
        <JourneyTextArea
          id="property-notes"
          rows={3}
          value={propertyForm.notes}
          onChange={onPropertyFormChange('notes')}
        />
      </div>

      <div className="subform-stack__actions">
        <button
          type="button"
          className="money-btn money-btn--secondary money-btn--sm"
          onClick={onAddPropertyRecord}
          disabled={isLoadingProperties || isSavingProperties}
        >
          Add property entry
        </button>
        <p>{propertyRecords.length} entries prepared</p>
      </div>

      {propertyRecords.length > 0 ? (
        <ul className="simple-record-list">
          {propertyRecords.map((record, index) => (
            <li key={`${record.id ?? 'draft'}-${index}`}>
              <div>
                <strong>{record.nameOfDeed || 'Property'}</strong>
                <p>
                  {record.areaDistrict || 'No area'} |{' '}
                  {onNormalizeFarmArrangement(
                    String(record.farmArrangement || ''),
                  ) || '-'}
                </p>
              </div>
              <button
                type="button"
                className="money-btn money-btn--danger money-btn--sm"
                onClick={() => {
                  void onRemovePropertyRecord(record);
                }}
                disabled={isSavingProperties}
              >
                Remove
              </button>
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  </div>
);
